/**
 * Group-stage standings table.
 *
 * Highlights:
 *  - Top 2 (qualified === "Q"):     green left-border
 *  - Best third (qualified === "Q3"): amber left-border
 *  - 3rd-placed in a non-best-third group: muted (still drawn, no border)
 *
 * Pass `compact={true}` to render the tooltip variant (smaller font, no header).
 */
import TeamCrest from "./TeamCrest";

function qualifiedStripe(q) {
    if (q === "Q") return "border-l-4 border-[#00FF66]";
    if (q === "Q3") return "border-l-4 border-[#FFB800]";
    return "border-l-4 border-transparent";
}

function qualifiedDot(q) {
    if (q === "Q") return <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00FF66] mr-2 align-middle" title="Top-2 advances" />;
    if (q === "Q3") return <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFB800] mr-2 align-middle" title="Best-third advances" />;
    return <span className="inline-block w-1.5 h-1.5 mr-2 align-middle" />;
}

export default function StandingsTable({ group, rows, teams, compact = false, t }) {
    const txt = (k, fallback) => (t ? t(k) : fallback);
    return (
        <div className={compact ? "text-[11px]" : "text-sm"}>
            {!compact && (
                <div className="font-heading font-extrabold uppercase tracking-tight text-[var(--text-primary)] text-lg mb-2">
                    {group}
                </div>
            )}
            <table className="w-full">
                <thead className="text-[var(--text-muted)] uppercase tracking-widest text-[10px]">
                    <tr>
                        <th className="text-left py-1 pl-3">#</th>
                        <th className="text-left py-1">{txt("standings.team", "Team")}</th>
                        <th className="text-center py-1 w-7" title={txt("standings.played_full", "Played")}>{txt("standings.played", "MP")}</th>
                        <th className="text-center py-1 w-7" title={txt("standings.won_full", "Won")}>{txt("standings.won", "W")}</th>
                        <th className="text-center py-1 w-7" title={txt("standings.drawn_full", "Drawn")}>{txt("standings.drawn", "D")}</th>
                        <th className="text-center py-1 w-7" title={txt("standings.lost_full", "Lost")}>{txt("standings.lost", "L")}</th>
                        <th className="text-center py-1 w-9" title="Goals for">GF</th>
                        <th className="text-center py-1 w-9" title="Goals against">GA</th>
                        <th className="text-center py-1 w-9" title={txt("standings.gd_full", "Goal difference")}>{txt("standings.gd", "GD")}</th>
                        <th className="text-right py-1 pr-3 w-10" title={txt("standings.points_full", "Points")}>{txt("standings.points", "Pts")}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const team = teams?.[r.team_id];
                        return (
                            <tr key={r.team_id} className={`${qualifiedStripe(r.qualified)} hover:bg-[var(--overlay-light)] transition-colors`}>
                                <td className="py-1.5 pl-3 text-[var(--text-secondary)] tabular-nums">{r.position}</td>
                                <td className="py-1.5">
                                    <span className="flex items-center gap-2 min-w-0">
                                        {qualifiedDot(r.qualified)}
                                        {team?.logo && <TeamCrest logo={team.logo} name={team.name} size="sm" />}
                                        <span className="truncate text-[var(--text-primary)]">{team?.name || r.team_id}</span>
                                    </span>
                                </td>
                                <td className="text-center tabular-nums text-[var(--text-secondary)]">{r.played}</td>
                                <td className="text-center tabular-nums text-[var(--text-secondary)]">{r.won}</td>
                                <td className="text-center tabular-nums text-[var(--text-secondary)]">{r.drawn}</td>
                                <td className="text-center tabular-nums text-[var(--text-secondary)]">{r.lost}</td>
                                <td className="text-center tabular-nums text-[var(--text-secondary)]">{r.gf}</td>
                                <td className="text-center tabular-nums text-[var(--text-secondary)]">{r.ga}</td>
                                <td className={`text-center tabular-nums ${r.gd > 0 ? "text-[#00FF66]" : r.gd < 0 ? "text-[var(--text-muted)]" : "text-[var(--text-secondary)]"}`}>
                                    {r.gd > 0 ? `+${r.gd}` : r.gd}
                                </td>
                                <td className="text-right pr-3 tabular-nums font-bold text-[var(--text-primary)]">{r.points}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {!compact && (
                <div className="text-[10px] text-[var(--text-muted)] mt-2 px-3 flex gap-3">
                    <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00FF66]" /> {txt("standings.legend_q", "Top-2 advance")}</span>
                    <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFB800]" /> {txt("standings.legend_q3", "Best-third advances")}</span>
                </div>
            )}
        </div>
    );
}
